/**
 * 上下文管理器
 * 职责：统一管理各种上下文（搜索、详情页、用户等）的生命周期
 */

export interface Context {
  type: "search" | "pin_detail" | "user" | "page";
  data: Record<string, any>;
  metadata: {
    createdAt: number;
    ttl: number; // 生存时间（毫秒），0 表示永不过期
    scope: "global" | "page" | "component"; // 作用域
    sessionId: string; // 会话ID
  };
}

export type ContextListener = (context: Context | null) => void;

export class ContextManager {
  private contexts: Map<string, Context> = new Map();
  private listeners: Map<string, Set<ContextListener>> = new Map();

  /**
   * 注册上下文
   */
  register(key: string, context: Context): void {
    this.contexts.set(key, context);
    this.notifyListeners(key, context);

    // 自动清理过期上下文
    if (context.metadata.ttl > 0) {
      setTimeout(() => {
        if (this.contexts.get(key) === context) {
          this.clear(key);
        }
      }, context.metadata.ttl);
    }
  }

  /**
   * 获取上下文
   */
  get(key: string): Context | null {
    const context = this.contexts.get(key);
    if (!context) return null;

    // 检查是否过期
    const age = Date.now() - context.metadata.createdAt;
    if (context.metadata.ttl > 0 && age > context.metadata.ttl) {
      this.clear(key);
      return null;
    }

    return { ...context };
  }

  /**
   * 合并上下文数据
   */
  merge(key: string, data: Partial<Context["data"]>): void {
    const context = this.get(key);
    if (!context) {
      console.warn(`[ContextManager] Cannot merge: context "${key}" not found`);
      return;
    }

    this.register(key, {
      ...context,
      data: { ...context.data, ...data },
    });
  }

  /**
   * 清除上下文
   */
  clear(key: string): void {
    this.contexts.delete(key);
    this.notifyListeners(key, null);
  }

  /**
   * 清除所有过期上下文
   */
  clearExpired(): void {
    const now = Date.now();
    this.contexts.forEach((context, key) => {
      const age = now - context.metadata.createdAt;
      if (context.metadata.ttl > 0 && age > context.metadata.ttl) {
        this.clear(key);
      }
    });
  }

  /**
   * 订阅上下文变化
   */
  subscribe(key: string, listener: ContextListener): () => void {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)!.add(listener);

    // 立即通知当前值
    listener(this.get(key));

    // 返回取消订阅函数
    return () => {
      this.listeners.get(key)?.delete(listener);
    };
  }

  /**
   * 通知监听器
   */
  private notifyListeners(key: string, context: Context | null): void {
    this.listeners.get(key)?.forEach((listener) => {
      try {
        listener(context);
      } catch (error) {
        console.error(`[ContextManager] Listener error for "${key}":`, error);
      }
    });
  }

  /**
   * 获取所有上下文键
   */
  getKeys(): string[] {
    return Array.from(this.contexts.keys());
  }

  /**
   * 清除所有上下文
   */
  clearAll(): void {
    const keys = this.getKeys();
    keys.forEach((key) => this.clear(key));
  }
}

// 导出单例
export const contextManager = new ContextManager();

// 定期清理过期上下文（每5分钟）
if (typeof window !== "undefined") {
  setInterval(() => {
    contextManager.clearExpired();
  }, 5 * 60 * 1000);
}
